import Vue from 'vue';
import VueSimpleSpinner from 'vue-simple-spinner';
import HttpMixin from '@/mixins/HttpMixin';
import UploadControl from '@/componentsReusable/UploadControl.vue';
import TaskColorControl from '@/componentsReusable/tasks/TaskColorControl.vue';
import flatPickr from 'vue-flatpickr-component';
import {BallPulseLoader} from 'vue-loaders';
import {updateTask} from '@/services/TaskService';
import {UpdateFields, Task as TaskModel} from '@/interfaces/TaskInterfaces';
import TaskChecklistControl from '@/componentsReusable/tasks/TaskChecklistControl.vue';
import {taskAssignGroups, taskAssignGroupsAdvancedSearch, UserRoleEnum} from "@/enums/userRolesEnum";
import {HTTP} from "@/utils/httpCommonUtil";
import GenericModal from '@/componentsReusable/genericModal.vue';
import {convertDateStringToUTCDate} from "@/utils/dateUtil";

export default Vue.component('task', {
  name: 'Task',
  props: {
    task: { type: Object },
    isNewTask: { type: Boolean, default: false},
    errors: {
      type: Object,
      default: () => {
        return {
          titleErrorMessage: '',
          descriptionErrorMessage: '',
          noteErrorMessage: '',
          dueDatetimeErrorMessage: '',
          remindDatetimeErrorMessage: '',
          assignedToAppUserIdErrorMessage: '',
          taskColumnIdErrorMessage: '',
          isEmergencyErrorMessage: '',
          isPrivateErrorMessage: ''
        };
      }
    },
    forceShowDetails: {
      type: Boolean,
      default: false
    },
    canOpenRelatedTask: {
      type: Boolean,
      default: true
    }
  },
  mixins: [HttpMixin],
  components: {
    VueSimpleSpinner,
    UploadControl,
    flatPickr,
    BallPulseLoader,
    TaskColorControl,
    TaskChecklistControl,
    GenericModal
  },
  data() {
    return {
      ourTask: (this as any).setTask(this.task),
      deleting: false,
      updating: false,
      isManager: false,
      showAttachmentConfirmationModal: false,
      showTaskConfirmationModal: false,
      pendingAttachmentDeleteId: null,
      childTitle: '',
      multiAssignUsers: {
        assignedToIdFilter: [],
        currentAssignedUserIdFilters: []
      }
    };
  },
  computed: {
    assignables(): any[] {
      const assignables = [...taskAssignGroups];
      assignables.push(...this.$store.state.user.users.map((user: any) => {
        return {
          appUserId: user.appUserId,
          fullname: user.firstName + ' ' + user.lastName
        }
      }));
      return assignables;
    },
    users(): any[] {
      const groups : any[] = [ ...taskAssignGroupsAdvancedSearch ];
      const userList = this.$store.state.user.users.map((user: any) => {
        return {
          id: user.appUserId,
          name: user.firstName + ' ' + user.lastName
        }
      });

      return groups.concat(userList);
    },
    showUnread(): boolean {
      return this.ourTask.id && this.isManager && !this.ourTask.assignedToSecurityRoleId && this.ourTask.assignedToAppUserId !== this.$store.state.user.appUserId;
    }
  },
  watch: {
    task(newTask: TaskModel) {
      // Someday we could warn the user that somebody changed the task behind their back
      this.ourTask = this.setTask(newTask);
    }
  },
  created() {
    this.isManager = this.$store.getters['user/hasRole']([UserRoleEnum.MANAGER]);
  },
  methods: {
    setTask(newTask: TaskModel) {
      const toReturn = Object.assign({}, newTask, { assignedTo: newTask.assignedToAppUserId || newTask.assignedToSecurityRoleId! * -1});
      // The following 2 fields are date-only so we need to convert them to UTC so they show the correct day.
      toReturn.dueDatetime = convertDateStringToUTCDate(toReturn.dueDatetime as any);
      toReturn.remindDatetime = convertDateStringToUTCDate(toReturn.remindDatetime as any);
      if (toReturn.childTasks) {
        toReturn.childTasks = toReturn.childTasks.map(child => Object.assign({}, child, {assignedTo: child.assignedToAppUserId || child.assignedToSecurityRoleId * -1}));
      }
      return toReturn;
    },
    userFullName(appUserId: number): string {
      const user = this.$store.getters['user/getUser'](appUserId);
      if (user) {
        return `${user.firstName} ${user.lastName}`;
      }
      return 'Unknown';
    },
    async columnChanged() {
      await this.updateField(UpdateFields.TASK_COLUMN_ID, { oldValue: this.task.taskColumnId, newValue: this.ourTask.taskColumnId });
    },
    async updateFieldForTask(field: UpdateFields, body: { oldValue?: any; newValue: any; other?: any }, taskId: number) {
      if (body.oldValue === body.newValue) {
        return true;
      }
      let success = false;
      try {
        if (!taskId) {
          return false;
        }
        this.updating = true;
        await updateTask(taskId, field, body);
        success = true;
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: err.response && err.response.data ? err.response.data.message : 'Failed to update Task.'
        });
        // Revert any changes
        this.ourTask = this.setTask(this.task);
      }
      this.updating = false;
      return success;
    },
    async updateField(field: UpdateFields, body: { oldValue?: any, newValue: any, other?: any }): Promise<boolean> {
      return await this.updateFieldForTask(field, body, this.ourTask.id);
    },
    async assignmentChanged() {
      if (await this.changeAssignment(this.ourTask)) {
        this.collapse();
      }
    },
    async changeAssignment(task: any) {
      let newValue = task.assignedTo;
      let field;
      if (newValue < 0) {
        newValue *= -1;
        field = UpdateFields.SECURITY_ASSIGNMENT;
        task.assignedToSecurityRoleId = newValue;
      } else {
        field = UpdateFields.USER_ASSIGNMENT;
        task.assignedToAppUserId = newValue;
      }
      return await this.updateFieldForTask(field, { newValue: newValue, other: task.updatedDatetime }, task.id);

    },
    async childAssignmentChanged(childTask: any) {
      await this.changeAssignment(childTask);
    },
    async titleBlur() {
      await this.updateField(UpdateFields.TITLE, {oldValue: this.task.title, newValue: this.ourTask.title});
    },
    async descriptionBlur() {
      await this.updateField(UpdateFields.DESCRIPTION, {oldValue: this.task.description, newValue: this.ourTask.description});
    },
    async noteBlur() {
      await this.updateField(UpdateFields.NOTES, {oldValue: this.task.note, newValue: this.ourTask.note});
    },
    async dueChanged(selectedDates: Date[]) {
      await this.updateDate(selectedDates, this.task.dueDatetime, UpdateFields.DUE_BY);
    },
    async updateDate(selectedDates: Date[], oldValue: any, updateField :UpdateFields) {
      if (selectedDates.length === 0) {
        return;
      }
      const selectedDate = selectedDates[0].toISOString().slice(0,10);
      if (oldValue && new Date(oldValue).toISOString().slice(0, 10) === selectedDate) {
        return;
      }
      await this.updateField(updateField, { oldValue: oldValue, newValue: selectedDate });
    },
    async remindChanged(selectedDates: Date[]) {
      await this.updateDate(selectedDates, this.task.remindDatetime, UpdateFields.REMIND_ON);
    },
    async emergencyChanged() {
      await this.updateField(UpdateFields.EMERGENCY, {oldValue: this.task.isEmergency, newValue: this.ourTask.isEmergency});
    },
    async privateChanged() {
      await this.updateField(UpdateFields.PRIVATE, {oldValue: this.task.isPrivate, newValue: this.ourTask.isPrivate});
    },
    /**
     * This is used by parent components to get the updated task (e.g. for when Creating the Task)
     */
    getTask(): TaskModel {
      if (this.ourTask.assignedToAppUserIdList && this.ourTask.assignedToAppUserIdList.length === 0) {
        this.ourTask.assignedToAppUserIdList.push(this.ourTask.assignedToAppUserId);
      }
      return this.ourTask;
    },
    async toggleComplete() {
      if (this.task.isComplete || !this.task.assignedToSecurityRoleId) {
        await this.updateField(UpdateFields.COMPLETE, { oldValue: this.task.isComplete, newValue: !this.task.isComplete});
      }
    },
    async changeColor(newColorId: number) {
      if (this.task.id) {
        await this.updateField(UpdateFields.COLOR, { newValue: newColorId, oldValue: this.task.taskColorId});
      } else {
        this.ourTask.taskColorId = newColorId;
      }
    },
    async markUnread() {
      await this.updateField(UpdateFields.IS_READ, {oldValue: true, newValue: false});
    },
    collapse() {
      if (this.ourTask.id) {
        this.$emit('collapse', this.ourTask.id);
      }
    },
    async associateUploadedFile (s3FilePath: string, fileName: string) {
      try {
        const response = await HTTP.post(`/api/tasks/${this.ourTask.id}/attachments`, {
          "attachmentName": fileName,
          "s3Key": s3FilePath
        });
        if (!response.data || !response.data.success) {
          throw new Error("Error associating uploaded file.");
        }
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded Attachment.'
        });
      }
    },
    async openAttachment (s3Key: string) {
      try {
        const response = await HTTP.get('/api/s3/signed-url/get?s3key=' + s3Key);
        if (!response.data || !response.data.data) {
          throw new Error("Error getting attachment.");
        }
        window.open(response.data.data, '_blank');
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded file attachment.'
        });
      }
    },
    async deleteAttachmentConfirmed () {
      this.showAttachmentConfirmationModal = false;
      await this.deleteAttachment(this.pendingAttachmentDeleteId, true);
    },
    async deleteAttachment (id: any, confirmed: boolean = false) {
      if (!confirmed) {
        this.showAttachmentConfirmationModal = true;
        this.pendingAttachmentDeleteId = id;
        return;
      } else {
        this.pendingAttachmentDeleteId = null;
      }
      await (this as any).httpDelete(`/tasks/${this.ourTask.id}/attachments/${id}`, `An error occurred deleting Attachment`);
    },
    async deleteTaskConfirmed () {
      this.showTaskConfirmationModal = false;
      await this.deleteTask(true);
    },
    async deleteTask(confirmed: boolean = false) {
      if (!confirmed) {
        this.showTaskConfirmationModal = true;
        return
      } else {
        this.deleting = true;
        try {
          await (this as any).httpDelete(`/tasks/${this.ourTask.id}`, 'Failed to delete Task.');
          this.$emit('task-deleted', this.ourTask);
        } finally {
          this.deleting = false;
        }
      }
    },

    toggleDetails() {
      this.ourTask.showDetails = !this.ourTask.showDetails;
      this.$emit('show-details', this.ourTask);
    },
    async addChildTask() {
      if (!this.childTitle) {
        return;
      }
      this.updating = true;
      const childId = await (this as any).httpPost(`/tasks/${this.ourTask.id}`, 'An error occurred creating Child Task.', { title: this.childTitle });
      if (childId) {
        this.childTitle = '';
      }
      this.updating = false;
    },
    openTask(childTaskId: number) {
      this.$emit('open-task', childTaskId);
    },
    browseToShift (shiftId: number) {
      window.open("/shifts/" + shiftId, '_blank');
    },
    generateId(fieldName:string) {
      return (fieldName + "-" + this.ourTask.id);
    },
    shouldTaskColumnBeDisabled(task: TaskModel) {
      return task.assignedToAppUserId && task.assignedToAppUserId !== this.$store.state.user.appUserId;
    },
    addFilter(filterList: any[], filter: any) {
      if (!filterList.some(((filterInstance: any) => filterInstance.id === filter.id))) {
        filterList.push(filter);
        this.ourTask.assignedToAppUserIdList.push(filter.id);

      }
    },

    removeFilter(filtersList: any[], filterIndex: number) {
      filtersList.splice(filterIndex, 1);
      this.ourTask.assignedToAppUserIdList.splice(filterIndex, 1);
    },
  }
});



// WEBPACK FOOTER //
// ./src/componentsReusable/tasks/Task.ts